import useBetaFeatures from "hooks/useBetaFeatures";

import { useUserSettingsDefaultsQuery } from "components/user/queries";

/**
 * Hook that determines Pad Scenario permissions based on beta features and user settings.
 *
 * @returns {Object} Permission object containing:
 *   - showFullPadScenario: boolean - Whether to show full editing capabilities
 *   - isReadonlyMode: boolean - Whether organization has readonly mode enabled
 *   - isStickManagementEnabled: boolean - Whether stick management feature is enabled
 *   - hasUserFeatureFlags: boolean - Whether user feature flags system is enabled
 *   - userAllowsFullScenario: boolean - Whether user has enabled full scenario access
 */
export const usePadScenarioPermissions = () => {
  const { hasFeature } = useBetaFeatures();
  const userSettingsDefaults = useUserSettingsDefaultsQuery();

  // Check if organization has readonly mode enabled (acts as kill switch)
  const isReadonlyMode = hasFeature("Pad Scenario Readonly");

  const isStickManagementMode = hasFeature("Stick Management");

  // Check user-level feature flag permissions
  const hasUserFeatureFlags = hasFeature("User Feature Flags");
  const userAllowsFullScenario =
    userSettingsDefaults.data?.FeatureFlag?.PadScenarioFull?.enabled ?? false;

  // Show full scenario if:
  // 1. NOT in readonly mode, AND NOT in stick management mode, AND
  // 2. Either no user feature flags system OR user has explicitly enabled full scenario
  const showFullPadScenario =
    !isReadonlyMode &&
    !isStickManagementMode &&
    (!hasUserFeatureFlags || userAllowsFullScenario);

  return {
    showFullPadScenario,
    isReadonlyMode,
    isStickManagementMode,
    hasUserFeatureFlags,
    userAllowsFullScenario
  };
};

export default usePadScenarioPermissions;
